import React from "react";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Download, TriangleAlert } from "lucide-react";

export interface CsvDiscardsFile {
  data: any;
  filename: string;
  type: string;
}

interface CsvDiscardsModalCstProps {
  open: boolean;
  discards: CsvDiscardsFile | null;
  onClose: () => void;
}

const CsvDiscardsModalCst: React.FC<CsvDiscardsModalCstProps> = ({
  open,
  discards,
  onClose,
}) => {
  const { t } = useTranslation("upload-modal");
  const theme = useTheme();

  const handleDownload = () => {
    if (!discards) return;
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, discards.data], { type: discards.type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = discards.filename || "discards.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogContent sx={{ padding: "24px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: alpha(theme.palette.warning.main, 0.12),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <TriangleAlert
            size={18}
            color={theme.palette.warning.main}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("discards-title")}
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: theme.palette.text.primary }}
            >
              {t("discards-text")}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={handleDownload}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                }}
              >
                {t("discards-download")}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "8px 24px 20px" }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
        >
          {t("discards-close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvDiscardsModalCst;
